//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { LocalDictionaryFactory } from '../../api/api.module'
import { Activity, Enums } from '../../../models/common'
import { OpConfigurationProfile, OpDataTypeInGroup } from '../../../models/core'

import { CreateEditInitParams } from '../../common/create-edit-popup/create-edit-popup.controller'
import { Details, DetailsScope, DetailsAccordion, DetailsAccordionSection } from '../../common/common.details'
import { DetailsParametersOptions } from '../../common/details-parameters/details-parameters.directive'
import { DetailsPopupOptions, DetailsOptions } from '../../common/details-popup/details-popup.directive'
import { LoggerService } from '../../common/common.module'

import { ConfigHelper, ConvertHelper, DataTypeGroupComponent, EventsNames, IUserSession, LocalizationHelper, LogHelper, OperatorLayoutSettings, TemplateHelper, ToastHelper, TypeHelper } from '../../../helpers'
import { ConfigurationProfileService } from '../../../services/configuration-profile'
import { ConfigurationProfileFunctionModel } from '../../../modules/api/api.models'

interface ConfigurationProfileDetailsScope extends DetailsScope {
    CONFIGURATION_PROFILE_COMMON_TAB_NAME: string

    CONFIGURATION_PROFILE_PARAMS_ACCORDION_NAME: string
    CONFIGURATION_PROFILE_ACCORDION_NAME: string

    id: number

    detailsParametersOptions: DetailsParametersOptions

    noActiveAccordions: string
    noDefinedContent: string

    configurationProfileDescription: string

    profileFunctionsList: ConfigurationProfileFunctionModel[]
    deviceFunctionTypes: {
        name: string;
        id: number;
    }[]
    getProfileFunctionNameText: (typeName: string) => string

    deleteConfirmationOptions: {
        title: string
        onConfirmed: () => void
    }
}

export class ConfigurationProfileDetailsDirectiveController extends Details<ConfigurationProfileDetailsScope> {

    // #region Members

    private referenceObject: OpConfigurationProfile
    private referenceObjectId: number

    // #endregion


    // #region Ctor

    constructor(
        $scope: ConfigurationProfileDetailsScope,
        $q: ng.IQService,
        $timeout: ng.ITimeoutService,
        private $sce: ng.ISCEService,
        logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        protected userSession: IUserSession,
        protected configurationProfileService: ConfigurationProfileService,
        protected referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $q, logger, localDictionary, OperatorLayoutSettings.ContactPopupTabActive, userSession, referenceType)

        this.setConfirmationsOptions()
        this.setEvents()
        this.setOptions()
        this.setTemplateConsts()

        if (this.userSession.getLoggedOperator().hasPermission(Activity.CONFIGURATION_PROFILE_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }

    // #endregion


    // #region Events

    private onOpen(options: DetailsOptions): void {
        this.$scope.detailsOpenParams = options

        this.referenceObjectId = options.referenceObjectId

        this.onClear()
        this.showPopup()
        this.onRefresh()
    }

    private onRefresh(toRefresh: OpConfigurationProfile = null): void {
        this.$scope.options.isDataLoaded = false

        const deferred: JQueryDeferred<OpConfigurationProfile> = $.Deferred<OpConfigurationProfile>()

        if (!toRefresh) {
            this.userSession
                .getDataProvider()
                .ConfigurationProfile
                .getConfigurationProfile(this.referenceObjectId)
                .then((result: OpConfigurationProfile) => {
                    this.$scope.popupTitle = result.toString()
                    deferred.resolve(result)
                })
                .fail((error: any) => {
                    const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                    ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                    LogHelper.warn(ConfigurationProfileService.LOG_PREFIX, `Unable to Load data. Error: ${errorMsg}.`)
                })
                .always(() => {
                    this.$scope.showTabs = true
                })
        } else {
            deferred.resolve(toRefresh)
        }

        deferred
            .promise()
            .then((result: OpConfigurationProfile) => {
                if (result && (!this.referenceObject || this.referenceObject.idConfigurationProfile == result.idConfigurationProfile)) {
                    this.referenceObject = result

                    if (result != null && result.configuationProfileFuntionsDescr != null) {
                        this.$scope.configurationProfileDescription = this.$sce.trustAsHtml(result.configuationProfileFuntionsDescr.description)
                    }

                    return $.when(
                        this.setBasicParameters(),
                    )
                }
            })
            .always(() => {
                this.$scope.options.isDataLoaded = true
            })
    }

    private onClear(): void {
        this.$scope.popupTitle = TypeHelper.emptyString
        this.$scope.showTabs = false

        this.referenceObject = null
        this.$scope.detailsParametersOptions = null
    }

    private onClose(): void {
        this.hidePopup()
    }

    private onConfirmDelete(): void {
        this.configurationProfileService
            .deleteConfigurationProfile(this.referenceObjectId)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
                this.onClose()
            })
            .fail((error: any) => {
                this.logger.log(error)
            })
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.detailsPopupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onEditConfigurationProfileParams(): void {
        const createEditInitParams: CreateEditInitParams = this.configurationProfileService.getCreateEditOptions(this.referenceObject.idConfigurationProfile)
        this.$scope.$root.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, createEditInitParams)
    }

    // #endregion

    // #region Setup   

    private setBasicParameters(): JQueryPromise<void> {
        const def: JQueryDeferred<void> = $.Deferred<void>()

        DataTypeGroupComponent
            .getDataTypeInGroupByModule(
                this.userSession.getDataProvider(),
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
                this.moduleName,
                Enums.ReferenceType.IdDistributor,
                this.userSession.getLoggedOperator().idDistributor
            )
            .then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                this.$scope.detailsParametersOptions = {
                    dataTypes: dataTypeInGroup,
                    referenceType: this.referenceType,
                    referenceObject: this.referenceObject,
                    useDynamicValues: true,
                    onDataLoaded: () => {
                        def.resolve()
                    }
                }
            })
            .catch(() => {
                def.reject()
            })

        if (this.isAccordionHidden(this.$scope.CONFIGURATION_PROFILE_PARAMS_ACCORDION_NAME)) {
            def.resolve()
        }

        return def.promise()
    }

    public getAccordions(): DetailsAccordion[] {
        const accordions: DetailsAccordion[] = []
        let items: DetailsAccordionSection[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.CONFIGURATION_PROFILE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ConfigurationProfileBaseParameters)) {
            items = []

            items.push({
                name: this.$scope.CONFIGURATION_PROFILE_PARAMS_ACCORDION_NAME,
                showInTab: this.$scope.CONFIGURATION_PROFILE_COMMON_TAB_NAME,
                showStatus: true,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BasicParameters),
                visible: true,
                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.CONFIGURATION_PROFILE_LIST)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.CONFIGURATION_PROFILE_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.ConfigurationProfileBaseParameters),
                editMethod: () => this.onEditConfigurationProfileParams(),

            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.ConsumerDetailsAccordions, 0, items))
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.CONFIGURATION_PROFILE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ConfigurationProfileDetailedParameters)) {
            items = []

            items.push({
                name: this.$scope.CONFIGURATION_PROFILE_ACCORDION_NAME,
                showInTab: this.$scope.CONFIGURATION_PROFILE_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Parameters),
                visible: false,
                canEdit: false
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.ConsumerDetailsAccordions, 1, items))
        }

        return accordions
    }

    public getTabs(): any[] {
        const tabs: any[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.CONFIGURATION_PROFILE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ConfigurationProfileBaseParameters)
            || !this.userSession.getLoggedOperator().hasPermission(Activity.CONFIGURATION_PROFILE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ConfigurationProfileDetailedParameters)
        ) {
            tabs.push({
                template: TemplateHelper.getContextMenuItem(
                    LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common),
                    ConfigHelper.Icon.HOME,
                    this.userSession
                ),
                name: this.$scope.CONFIGURATION_PROFILE_COMMON_TAB_NAME
            })
        }

        return tabs
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),
            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            (event: ng.IAngularEvent, ...args: any[]) => this.onOpen(args[3])
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            () => this.onClose()
        )

        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => this.onRefresh(args[0]))

        this.$scope.getProfileFunctionNameText = (typeName: string): string => {
            return LocalizationHelper.getLocalizedText(typeName)
        }
    }

    private setOptions(): void {

        this.$scope.options = <DetailsPopupOptions>{
            id: this.$scope.id,
            closeEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName),
            delHasPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.CONFIGURATION_PROFILE_DEL) ? Activity[Activity.CONFIGURATION_PROFILE_DEL] : null,
            openEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName),
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DETAILS, this.moduleName),
            deleteMethod: () => this.onDelete(),
            getAccordionDataMethod: () => this.getAccordionData(),
            getAccordionSelectionMethod: () => this.getAccordionSelection(),
            updateAccordionMethod: (names: any[]) => this.updateAccordions(names)
        }
    }

    private setTemplateConsts(): void {
        this.$scope.CONFIGURATION_PROFILE_COMMON_TAB_NAME = 'common'
        this.$scope.CONFIGURATION_PROFILE_PARAMS_ACCORDION_NAME = 'CONFIGURATION_PROFILE_PARAMS_ACCORDION_NAME'
        this.$scope.CONFIGURATION_PROFILE_ACCORDION_NAME = 'CONFIGURATION_PROFILE_ACCORDION_NAME'
        this.$scope.noActiveAccordions = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoActiveAccordions)
        this.$scope.noDefinedContent = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoDefinedContent)
    }


    // #endregion

    resetData() { this.onClear() }//DO USUNIECIA!!! - zastąpić onClear
    loadData(id: number, objects: any[]) { this.onOpen(null) }//DO USUNIECIA!!! - zastąpić onOpen
    updateData(result: any) { this.onRefresh(result) }//DO USUNIECIA!!! - zastąpić onRefresh
}